import { Inject, Injectable } from '@angular/core';
import {
  anyAsset,
  AssetListFileObject,
  CanPipe,
  ClientEnvironment,
  ENVIRONMENT,
  FormControlModel,
  StepperGroupModel,
  StepperModel,
  ValueListsService,
  WidthClass,
} from '@thehubz/client/core';
import { CollectionsApiService, UserInfo, UserValue } from '@thehubz/client/data-access-api';
import { CustomClients } from '@thehubz/shared/branding';
import { AnyType, AssetMetadata } from '@thehubz/shared/model';
import { lastValueFrom, map, Observable, take } from 'rxjs';
import { customFields } from '../../../customer/customer-asset-fields';
import { lufthansaGeneralInformationFields } from '../../../customer/lufthansa/lufthansa-form-control.model';
import { vorwerkGeneralInformationFields, vorwerkTaggingNTaxonomyFields } from '../../../customer/vorwerk/vorwerk-form-control.model';
import { StepperConfig } from '../../../models/stepper-config.interface';
import { AssetCommonService } from '../../../services/asset-common.service';
import { seatContentDescriptionFields, seatGeneralInformationFields, seatTaggingNTaxonomyFields } from './../../../customer/seat/seat-form-fields';
import { vkbGeneralInformationFields, vkbUsageRightsFields } from './../../../customer/vkb/vkb-form-fields';
import { AssetBuyoutRightsStepperConfigService } from './asset-buyout-rights-stepper-config.service';
import {
  arvalUsageRightsFields,
  createNewCollectionStepperConfig,
  productionDetailsStepperConfigs,
  stepperConfigurations,
  usageRightConfigs,
} from './asset-settings-stepper.constants';

@Injectable({
  providedIn: 'root',
})
export class AssetSettingsStepperConfigService {
  existingCollectionOption = {
    id: 'add2ExistingCollection',
    name: $localize`Add to existing collection`,
  };

  AnyAsset = anyAsset;

  constructor(
    private assetCommonService: AssetCommonService,
    @Inject(ENVIRONMENT) private environment: ClientEnvironment,
    private assetBuyoutRightsStepperConfigService: AssetBuyoutRightsStepperConfigService,
    private collectionAPIService: CollectionsApiService,
    private valueListsService: ValueListsService,
    private canPipe: CanPipe,
  ) {}
  /* Standard Asset Stepper Config */
  /* This function is used to create the stepper config for the asset upload component. */
  /************************/
  getStandardAssetSettingsStepperConfig = async (users: UserInfo[], stepperConfig?: StepperConfig, standardClientFlag = true): Promise<StepperModel> => {
    let uploadStepperConfig: StepperModel = stepperConfigurations(users, stepperConfig);

    this.updateStandardClientStatus(uploadStepperConfig);

    if (CustomClients.WestLotto === this.environment.brandingKey) {
      standardClientFlag = false;
      const tagStepperForm = this.findStepperForm(uploadStepperConfig.stepper, 'tags');
      // Get the westLotto tags custom fields
      const westLottoTagsCustomFormFields: FormControlModel[] = JSON.parse(JSON.stringify(customFields.get(CustomClients.WestLotto)));
      // Set widthClass of the FormFields
      westLottoTagsCustomFormFields.forEach((field) => {
        field.widthClass = WidthClass.W12;
      });
      // push the westLotto tags custom fields to standard tag stepper
      tagStepperForm?.push(...westLottoTagsCustomFormFields);
    }

    if (standardClientFlag) {
      this.insertProductionDetails(uploadStepperConfig);
      this.addBuyoutRightsStepper(uploadStepperConfig, {
        initialStepperId: '6',
        insertIndex: 5,
        stepperGetter: (lastStepperId) =>
          this.assetBuyoutRightsStepperConfigService.getAssetBuyoutRightsMakeOSStepperConfig(
            [],
            false,
            lastStepperId,
            !this.environment.uploadStepperConf?.buyoutRightSingleStepper,
          ),
      });
    }

    await this.appendExistingStandardCollectionOption(uploadStepperConfig);

    uploadStepperConfig = this.stepperAccessibility(uploadStepperConfig);
    this.updateStepperActionGoToStep(uploadStepperConfig);

    return uploadStepperConfig;
  };

  private updateStandardClientStatus(stepperConfig: StepperModel): void {
    const generalInformationStepper = this.findStepper(stepperConfig.stepper, 'generalInformation');
    this.updateStatus(CustomClients.Standard, generalInformationStepper?.formFields);
  }

  private insertProductionDetails(stepperConfig: StepperModel): void {
    stepperConfig.stepper.splice(2, 0, productionDetailsStepperConfigs());
  }

  private async appendExistingStandardCollectionOption(stepperConfig: StepperModel): Promise<void> {
    const filterObject = {
      filters: {
        ownerID: this.assetCommonService.currentUser.userData._id,
      },
    };

    const collectionList = await lastValueFrom(this.collectionAPIService.collectionsControllerFindAll(JSON.stringify(filterObject)));

    if (collectionList.result.length === 0) return;

    const lastStepperIndex = stepperConfig.stepper.length - 1;

    if (lastStepperIndex < 0) return;

    const collectionFormField = stepperConfig.stepper[lastStepperIndex]?.formFields;
    const options = collectionFormField?.[0]?.options;

    if (options) {
      options.push(this.existingCollectionOption);
    }
  }

  getAllianzAssetSettingsStepperConfig = async (users: UserInfo[], stepperConfig?: StepperConfig): Promise<StepperModel> => {
    // Get the standard configuration
    const standardConfig = await this.getStandardAssetSettingsStepperConfig(users, stepperConfig, false);
    // Deep copy of the standard configuration
    let allianzAssetsStepperConfig: StepperModel = JSON.parse(JSON.stringify(standardConfig));
    // Get the General information stepper forms
    const generalInformationStepperForm = this.findStepperForm(allianzAssetsStepperConfig.stepper, 'generalInformation');
    // remove owner
    this.removeGeneralInformationOwner(generalInformationStepperForm);
    // add usage rights
    allianzAssetsStepperConfig.stepper.splice(2, 0, usageRightConfigs(users));
    // return only those stepper configs in which user is allowed
    allianzAssetsStepperConfig = this.stepperAccessibility(allianzAssetsStepperConfig);
    // re-sequence the stepperId in stepper config;
    // update the stepper config's GoToStep value for next and back button
    this.updateStepperActionGoToStep(allianzAssetsStepperConfig);
    // return arval stepper config
    return allianzAssetsStepperConfig;
  };

  /**
   * This function is used to create the stepper config for Lufthansa customer for the asset upload component.
   */
  getLufthansaAssetSettingsStepperConfig = async (users: UserInfo[], stepperConfig?: StepperConfig): Promise<StepperModel> => {
    // Get the standard configuration
    const standardConfig = await this.getStandardAssetSettingsStepperConfig(users, stepperConfig, false);
    // Deep copy of the standard configuration
    let lufthansaAssetsStepperConfig: StepperModel = JSON.parse(JSON.stringify(standardConfig));
    // Get the General information stepper forms
    const generalInformationStepper = this.findStepper(lufthansaAssetsStepperConfig.stepper, 'generalInformation');
    // Hide 'agency', 'client', 'brand', fields
    if (generalInformationStepper) generalInformationStepper.formFields = lufthansaGeneralInformationFields();
    this.updateStatus(CustomClients.LufthansaMediacenter, generalInformationStepper?.formFields);
    generalInformationStepper?.formFields?.forEach((formField) => {
      if (['agency', 'client'].includes(formField.formFieldName || '')) {
        formField.visible = false;
        if (formField.validators) formField.validators.required = false;
      }
      if (formField.accessibilityRole && !this.canPipe.transform(this.AnyAsset, formField.accessibilityRole)) {
        formField.visible = false;
      }
    });

    /* Tags & taxonomy update */
    /************************/
    // hide relation step for lufthansa
    lufthansaAssetsStepperConfig.stepper = lufthansaAssetsStepperConfig.stepper.filter(
      (step) => !['selectAssets', 'defineAssetRelation'].includes(step.stepperGroupName),
    );
    // return only those stepper configs in which user is allowed
    lufthansaAssetsStepperConfig = this.stepperAccessibility(lufthansaAssetsStepperConfig);
    // re-sequence the stepperId in stepper config;
    // update the stepper config's GoToStep value for next and back button
    this.updateStepperActionGoToStep(lufthansaAssetsStepperConfig);
    // return arval stepper config
    return lufthansaAssetsStepperConfig;
  };

  /* VKB Asset Stepper Config */
  /* This function is used to create the stepper config for Lufthansa customer for the asset upload component. */
  getVKBAssetSettingsStepperConfig = async (users: UserInfo[], stepperConfig?: StepperConfig): Promise<StepperModel> => {
    let vkbAssetsStepperConfig: StepperModel = await this.getDeepCopiedStandardConfig(users, stepperConfig);

    this.configureVKBGeneralInformationStepper(vkbAssetsStepperConfig);
    /* Tags & taxonomy update */
    /************************/
    this.configureVKBTagsAndTaxonomyStepper(vkbAssetsStepperConfig);
    /* Usage Rights update */
    /************************/
    this.configureVKBUsageRightsStepper(vkbAssetsStepperConfig, users);
    // hide relation step for VKB
    this.hideSteppers(vkbAssetsStepperConfig, ['selectAssets', 'defineAssetRelation']);
    // return only those stepper configs in which user is allowed
    vkbAssetsStepperConfig = this.stepperAccessibility(vkbAssetsStepperConfig);
    // re-sequence the stepperId in stepper config;
    // update the stepper config's GoToStep value for next and back button
    this.updateStepperActionGoToStep(vkbAssetsStepperConfig);
    // return arval stepper config
    return vkbAssetsStepperConfig;
  };

  private async getDeepCopiedStandardConfig(users: UserInfo[], stepperConfig?: StepperConfig): Promise<StepperModel> {
    const standardConfig = await this.getStandardAssetSettingsStepperConfig(users, stepperConfig, false);
    return JSON.parse(JSON.stringify(standardConfig));
  }

  private configureVKBGeneralInformationStepper(stepperConfig: StepperModel): void {
    const generalInfoStepper = this.findStepper(stepperConfig.stepper, 'generalInformation');
    if (!generalInfoStepper) return;

    generalInfoStepper.formFields = vkbGeneralInformationFields();
    this.updateStatus(CustomClients.VKB, generalInfoStepper.formFields);

    for (const field of generalInfoStepper.formFields) {
      if (['agency', 'client', 'brand'].includes(field.formFieldName || '')) {
        field.visible = false;
        if (field.validators) {
          field.validators.required = false;
        }
      }
    }
  }

  private configureVKBTagsAndTaxonomyStepper(stepperConfig: StepperModel): void {
    const tagStepperForm = this.findStepperForm(stepperConfig.stepper, AssetMetadata.General.TAGS);
    if (!tagStepperForm) return;

    const tagIndex = tagStepperForm.findIndex((field: AnyType) => field.formFieldName === AssetMetadata.General.TAGS);
    if (tagIndex !== -1) {
      const tagField = tagStepperForm[tagIndex];
      tagField.fieldLabel = $localize`Tags/Schlagworte`;
      tagField.infotext =
        'Als Tags/Schlagworte können z. B. beschreibende Elemente der Assets, das Produkt, Produktgruppe, für die das Asset verwendet wird, angegeben werden. Je mehr beschreibende Tags, umso besser. Bitte Groß-/Kleinschreibung beim Anlegen neuer Schlagworte beachten! Schreibfehler kann man selbst korrigieren oder den Tag wieder löschen. Um einen neuen Tag hinzuzufügen, geben Sie diesen in der "Suche" (Pfeil nach unten) ein und bestätigen Sie die Eingabe durch drücken der "Enter"-Taste.';
      tagField.mandatory = true;
      tagField.infotextExternalLink = true;

      if (!tagField.validators) {
        tagField.validators = { required: true, minLength: 6 };
      }
    }
  }

  private configureVKBUsageRightsStepper(stepperConfig: StepperModel, users: UserInfo[]): void {
    stepperConfig.stepper.splice(2, 0, usageRightConfigs(users));

    const usageRightForm = this.findStepperForm(stepperConfig.stepper, 'usageRight');
    if (usageRightForm) {
      usageRightForm.splice(0, usageRightForm.length, ...vkbUsageRightsFields());
    }
  }

  /**
   * This function is used to create the stepper config for SEAT customer for the asset upload component.
   */
  getSeatAssetSettingsStepperConfig = async (users: UserInfo[], stepperConfig?: StepperConfig): Promise<StepperModel> => {
    // Get the standard configuration
    const standardConfig = await this.getStandardAssetSettingsStepperConfig(users, stepperConfig, false);
    // Deep copy of the standard configuration
    const seatAssetsStepperConfig: StepperModel = JSON.parse(JSON.stringify(standardConfig));

    /* General information */
    /************************/
    this.updateSeatGeneralInformationStepper(seatAssetsStepperConfig);

    /* Content description*/
    /************************/
    this.updateSeatContentDescriptionStepper(seatAssetsStepperConfig);

    /* Tags & taxonomy update */
    /************************/
    this.updateSeatTagsAndTaxonomyStepper(seatAssetsStepperConfig);

    /* hide relation step for SEAT */
    /************************/
    this.hideSteppers(seatAssetsStepperConfig, ['selectAssets', 'defineAssetRelation', 'usageRight']);

    /* Add buyout rights stepper if enabled */
    /************************/
    this.addBuyoutRightsStepper(seatAssetsStepperConfig, {
      initialStepperId: '3',
      insertIndex: 2,
      stepperGetter: (lastStepperId) => this.assetBuyoutRightsStepperConfigService.getAssetSeatBuyoutRightsStepperConfig([], false, lastStepperId),
    });

    /* Perform final configuration adjustments */
    /************************/
    this.finalizeSeatStepperConfig(seatAssetsStepperConfig);

    // return arval stepper config
    return seatAssetsStepperConfig;
  };

  private updateSeatGeneralInformationStepper(stepperConfig: StepperModel): void {
    const generalInformationStepper = this.findStepper(stepperConfig.stepper, 'generalInformation');
    if (generalInformationStepper) {
      generalInformationStepper.stepperName = 'Marke, Modelle, Sondermodelle';
      generalInformationStepper.formFields = seatGeneralInformationFields();
      this.updateStatus(CustomClients.SEAT, generalInformationStepper.formFields);
    }
  }

  private updateSeatContentDescriptionStepper(stepperConfig: StepperModel): void {
    const contentDescriptionStepperForm = this.findStepperForm(stepperConfig.stepper, 'contentDescription');
    if (contentDescriptionStepperForm) {
      contentDescriptionStepperForm.splice(0, contentDescriptionStepperForm.length, ...seatContentDescriptionFields());
    }
  }

  private updateSeatTagsAndTaxonomyStepper(stepperConfig: StepperModel): void {
    const tagStepper = this.findStepper(stepperConfig.stepper, AssetMetadata.General.TAGS);
    if (tagStepper) {
      tagStepper.stepperId = '3';
    }
    const tagStepperForm = this.findStepperForm(stepperConfig.stepper, AssetMetadata.General.TAGS);
    if (tagStepperForm) {
      tagStepperForm.push(...seatTaggingNTaxonomyFields());
    }
  }

  private finalizeSeatStepperConfig(stepperConfig: StepperModel): void {
    // return only those stepper configs in which user is allowed
    stepperConfig = this.stepperAccessibility(stepperConfig);
    // re-sequence the stepperId in stepper config;
    // update the stepper config's GoToStep value for next and back button
    this.updateStepperActionGoToStep(stepperConfig);
  }

  /**
   * This function is used to create the stepper config for Arval customer for the asset upload component.
   */
  getArvalAssetSettingsStepperConfig = async (users: UserInfo[], stepperConfig?: StepperConfig): Promise<StepperModel> => {
    // Get the standard configuration
    const standardConfig = await this.getStandardAssetSettingsStepperConfig(users, stepperConfig, false);
    // Deep copy of the standard configuration
    let arvalAssetsStepperConfig: StepperModel = JSON.parse(JSON.stringify(standardConfig));
    // Get the General information stepper forms
    const generalInformationStepperForm = this.findStepperForm(arvalAssetsStepperConfig.stepper, 'generalInformation');
    // remove owner
    this.removeGeneralInformationOwner(generalInformationStepperForm);
    // Hide 'agency', 'client', 'brand', fields
    generalInformationStepperForm?.forEach((formField) => {
      if (['agency', 'client', 'brand', 'publishStartDate', 'publishEndDate'].includes(formField.formFieldName || '')) {
        formField.visible = false;
        if (formField.validators) formField.validators.required = false;
      }
    });
    this.updateStatus(CustomClients.Arval, generalInformationStepperForm);

    /* Tags & taxonomy update */
    // Get the tags stepper forms
    const tagStepperForm = this.findStepperForm(arvalAssetsStepperConfig.stepper, AssetMetadata.General.TAGS);
    // Get the arval tags custom fields
    const arvalTagsCustomFormFields: FormControlModel[] = JSON.parse(JSON.stringify(customFields.get(CustomClients.Arval)));
    // Set widthClass of the FormFields
    arvalTagsCustomFormFields.forEach((field) => {
      field.widthClass = WidthClass.W12;
    });
    // push the arval tags custom fields to standard tag stepper
    tagStepperForm?.push(...arvalTagsCustomFormFields);
    /************************/

    /* Usage rights update */
    arvalAssetsStepperConfig.stepper.splice(2, 0, usageRightConfigs(users));
    // Get the usage rights stepper forms
    const usageRightsStepperForm = this.findStepperForm(arvalAssetsStepperConfig.stepper, 'usageRight');
    // Get Arval specific usage right forms
    const arvalUsageRightCustomFormFields = arvalUsageRightsFields();
    // insert after owner; the arval usage rights custom fields to standard usage rights stepper
    usageRightsStepperForm?.splice(1, 0, ...arvalUsageRightCustomFormFields);
    /************************/

    // hide relation step for arval
    arvalAssetsStepperConfig.stepper = arvalAssetsStepperConfig.stepper.filter(
      (step) => !['selectAssets', 'defineAssetRelation'].includes(step.stepperGroupName),
    );
    // return only those stepper configs in which user is allowed
    arvalAssetsStepperConfig = this.stepperAccessibility(arvalAssetsStepperConfig);
    // re-sequence the stepperId in stepper config;
    // update the stepper config's GoToStep value for next and back button
    this.updateStepperActionGoToStep(arvalAssetsStepperConfig);
    // return arval stepper config
    return arvalAssetsStepperConfig;
  };

  /**
   * This function is used to create the stepper config for vorwerk customer for the asset upload component.
   */
  getVorwerkAssetSettingsStepperConfig = async (users: UserInfo[], stepperConfig?: StepperConfig): Promise<StepperModel> => {
    // Get the standard configuration
    const standardConfig = await this.getStandardAssetSettingsStepperConfig(users, stepperConfig, false);
    // Deep copy of the standard configuration
    const vorwerkAssetsStepperConfig: StepperModel = JSON.parse(JSON.stringify(standardConfig));
    /* General Information update */
    const generalInformationStepper = this.findStepper(vorwerkAssetsStepperConfig.stepper, 'generalInformation');
    if (generalInformationStepper) generalInformationStepper.formFields = vorwerkGeneralInformationFields();
    this.updateStatus(CustomClients.Vorwerk, generalInformationStepper?.formFields);
    /************************/

    /* Tags & taxonomy update */
    const tagStepperForm = this.findStepperForm(vorwerkAssetsStepperConfig.stepper, AssetMetadata.General.TAGS);
    if (tagStepperForm) tagStepperForm.push(...vorwerkTaggingNTaxonomyFields());

    // return vorwerk stepper config
    return vorwerkAssetsStepperConfig;
  };

  getCreateNewCollectionStepperConfig = async (assetListForCollection: AssetListFileObject[], brandingKey: CustomClients): Promise<StepperGroupModel> => {
    const usersLikeMe = await lastValueFrom(this.valueListsService.listUsersLikeMe(['global.library'], false, true));
    return createNewCollectionStepperConfig(assetListForCollection, usersLikeMe, brandingKey);
  };

  findStepper = (stepper: StepperGroupModel[], indexKey: string) => {
    const stepperGroupIndex = stepper.findIndex((step) => step.stepperGroupName === indexKey);
    return stepperGroupIndex !== -1 ? stepper[stepperGroupIndex] : undefined;
  };

  /**
   * Find stepper forms based on the key provided
   * @param stepper
   * @param indexKey
   * @returns
   */
  findStepperForm = (stepper: StepperGroupModel[], indexKey: string) => {
    return this.findStepper(stepper, indexKey)?.formFields;
  };

  /**
   * Sequence stepper id if any stepper is hidden due to user accessibility
   * @param uploadStepperConfig
   */
  updateStepperActionGoToStep(uploadStepperConfig: StepperModel): void {
    // re-sequence the stepperId in stepper config;
    uploadStepperConfig.stepper.forEach((step, i) => (step.stepperId = (i + 1).toString()));
    // update the stepper config's GoToStep value for next and back button
    uploadStepperConfig.stepper.forEach((step, i) => {
      step.actions?.forEach((action) => {
        if (action.actionType === 'next' || action.actionLabel === 'Next') {
          action.goToStep = uploadStepperConfig.stepper[i + 1]?.stepperId ? +uploadStepperConfig.stepper[i + 1].stepperId : action.goToStep;
        }
        if (action.actionType === 'back' || action.actionLabel === 'Back') {
          action.goToStep = uploadStepperConfig.stepper[i - 1]?.stepperId ? +uploadStepperConfig.stepper[i - 1].stepperId : action.goToStep;
        }
      });
    });
  }

  /**
   * Check stepper role property if user is allowed to see certain steps
   * @param uploadStepperConfig
   * @returns
   */
  stepperAccessibility(uploadStepperConfig: StepperModel): StepperModel {
    uploadStepperConfig.stepper = uploadStepperConfig.stepper.filter((step) => this.canPipe.transform(this.AnyAsset, step.role || 'upload'));
    return uploadStepperConfig;
  }

  updateStatus(brandingKey: string, formFields: FormControlModel[] | undefined) {
    const statusField = formFields?.find((field) => field.formFieldName === 'status');
    if (statusField) statusField.options = [];
    switch (brandingKey) {
      case CustomClients.Standard:
        statusField?.options?.push(...this.assetCommonService.getStandardAssetFilterStatusList(this.canPipe, this.AnyAsset));
        break;
      case CustomClients.Vorwerk:
        statusField?.options?.push(...this.assetCommonService.getVorwerkAssetFilterStatusList(this.canPipe, this.AnyAsset));
        break;
      case CustomClients.LufthansaMediacenter:
      case CustomClients.LufthansaMedialounge:
        statusField?.options?.push(...this.assetCommonService.getLufthansaAssetFilterStatusList(this.canPipe, this.AnyAsset));
        break;
      case CustomClients.VKB:
        statusField?.options?.push(...this.assetCommonService.getVKBAssetFilterStatusList(this.canPipe, this.AnyAsset));
        break;
      case CustomClients.SEAT:
        statusField?.options?.push(...this.assetCommonService.getSEATAssetFilterStatusList(this.canPipe, this.AnyAsset));
        break;
      case CustomClients.Arval:
        statusField?.options?.push(...this.assetCommonService.getArvalAssetFilterStatusList(this.canPipe, this.AnyAsset));
        break;
    }
  }

  populateUsers(): Observable<UserValue[]> {
    return this.valueListsService.listUsers().pipe(
      take(1), // Ensure the observable completes after the first emission
      map((users) => users),
    );
  }

  removeGeneralInformationOwner(generalInformationStepperForm: FormControlModel[] | undefined) {
    // find the index of owner FormField in owners array
    const ownerIndex = generalInformationStepperForm?.findIndex((form) => form.formFieldName === 'owner') || 0;
    // remove the owner field from generalInformationStepperForm because we are keeping the owner field with usage right stepper
    if (ownerIndex !== -1) generalInformationStepperForm?.splice(ownerIndex, 1);
  }

  private addBuyoutRightsStepper(
    stepperConfig: StepperModel,
    options: {
      initialStepperId: string;
      insertIndex: number;
      stepperGetter: (lastStepperId: string) => StepperModel;
    },
  ): void {
    let lastStepperId = options.initialStepperId;
    const collectionStepper = this.findStepper(stepperConfig.stepper, 'collection');

    if (this.environment.uploadStepperConf?.buyoutRightSingleStepper) {
      const buyoutRightsStepper: StepperModel = options.stepperGetter(lastStepperId);

      if (buyoutRightsStepper) {
        stepperConfig.stepper.splice(options.insertIndex, 0, buyoutRightsStepper.stepper[0]);
      }

      lastStepperId = (parseInt(lastStepperId) + 1).toString();
    }

    if (collectionStepper) {
      collectionStepper.stepperId = lastStepperId;
    }
  }

  private hideSteppers(stepperConfig: StepperModel, fieldsToHide: string[]): void {
    stepperConfig.stepper = stepperConfig.stepper.filter((step) => !fieldsToHide.includes(step.stepperGroupName));
  }
}
